/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import './style/connectionComplete.scss';

import React, {MouseEventHandler, ReactElement} from 'react';
import fileTextFilled from '@iconify/icons-ant-design/file-text-filled';
import Bell from '@iconify/icons-mdi/bell-outline';
import {formatInvoiceNumber, replaceDateVariables} from "../../util/utils";
import {AwesomeIcon, ChangeFunction, ChangeKeyFunction, Checkbox, Loader, voidFunction} from '@dvrd/dvr-controls';
import BolmateRadio from '../controls/radio/bolmateRadio';
import BolmateButton from '../controls/button/bolmateButton';
import {Icon} from '@iconify/react';
import classNames from 'classnames';
import {BolmateInput, BolmateNumberInput} from '../controls';
import Account from '../../models/accountModel';
import {AutoUploadType, InvoiceSettingsErrors, ResetErrorHandler} from '../../util/interfaces';
import {ConnectionSettingsStep, InvoiceNumberType} from "./connectionCompleteController";
import Image from "../misc/image";
import {NotificationType} from "../../util/enums";
import {NotificationTypeParam} from "../../util/types";
import BetaNotificationLabel from '../notifications/betaNotificationLabel';

interface Props {
    onSelectInvoiceNumberType: (numType: InvoiceNumberType) => MouseEventHandler;
    onClickNext: MouseEventHandler;
    onClickBack: MouseEventHandler;
    onChangeInvoiceNum: (nextNum: string) => void;
    onChangeSetting: ChangeKeyFunction;
    onChangeImportHistory: (import30Days: boolean) => MouseEventHandler;
    onResetError: ResetErrorHandler<InvoiceSettingsErrors>;
    onChooseAutoUpload: (value: AutoUploadType, skip?: boolean) => MouseEventHandler;
    onChangeAutoUploadOld: (value: boolean | null) => MouseEventHandler;
    onChangeOldInvoiceNumStart: ChangeFunction<string>;
    onChangeOldInvoiceNumPrefix: ChangeFunction<string>;
    onToggleNotification: (notificationType: NotificationType) => MouseEventHandler;
    importHistory: boolean | null;
    invoiceNumberType: InvoiceNumberType | null;
    account: Account;
    errors: InvoiceSettingsErrors;
    invoicePreview: string;
    invoicePrefix: string;
    invoiceStart: string;
    step: ConnectionSettingsStep;
    loading: boolean;
    autoUpload: AutoUploadType | null;
    autoUploadOld: boolean | null;
    oldInvoiceNumPrefix: string;
    oldInvoiceNumStart: string;
    enableNotifications: Set<NotificationType>;
}

export default function ConnectionComplete(props: Props) {
    const {
        onSelectInvoiceNumberType, onClickNext, onClickBack, onChangeInvoiceNum, onChangeSetting, onChangeImportHistory,
        onResetError, importHistory, invoiceNumberType, account, invoicePreview, invoicePrefix, step, loading,
        invoiceStart, errors, onChooseAutoUpload, autoUpload, oldInvoiceNumStart, oldInvoiceNumPrefix,
        autoUploadOld, onChangeAutoUploadOld, onChangeOldInvoiceNumPrefix, onChangeOldInvoiceNumStart,
        onToggleNotification, enableNotifications
    } = props;


    function renderYesButton(onClick: MouseEventHandler, value: boolean) {
        return (
            <div className='body-container'>
                <div className='flow' onClick={onClick}>
                    <div className='texts-container'>
                        <label className='flow-title'>Ja</label>
                    </div>
                    <BolmateRadio onSelect={voidFunction} selected={value}/>
                </div>
            </div>
        )
    }

    function renderNoButton(onClick: MouseEventHandler, value: boolean) {
        return (
            <div className='body-container'>
                <div className='flow' onClick={onClick}>
                    <div className='texts-container'>
                        <label className='flow-title'>Nee</label>
                    </div>
                    <BolmateRadio onSelect={voidFunction} selected={value}/>
                </div>
            </div>
        )
    }

    function renderBackButton() {
        return (
            <BolmateButton onClick={onClickBack} label='Terug' secondary
                           ornaments={{element: <AwesomeIcon name='arrow-left'/>}}/>
        )
    }

    function renderInvoicePreview(invoicePrefix: string) {
        const count = Number(invoicePreview);
        const editable = !Number.isNaN(count);
        const onUp = () => {
            const nextCount = count + 1;
            const nextNum = formatInvoiceNumber(nextCount, invoiceStart)
            onChangeInvoiceNum(nextNum);
        };
        const onDown = () => {
            const nextCount = count - 1
            const nextNum = formatInvoiceNumber(nextCount, invoiceStart);
            onChangeInvoiceNum(nextNum);
        };
        const prefix = replaceDateVariables(invoicePrefix);
        const min = Number(invoiceStart);
        return <div className='invoice-number-preview'>
            <label className='preview'>Voorbeeld: {prefix}{invoicePreview}</label>
            {editable && (
                <div className='editors'>
                    <span className='editor' onClick={onUp}>+</span>
                    <span className={classNames('editor', count <= min && 'disabled')}
                          onClick={count > min ? onDown : voidFunction}>-</span>
                </div>
            )}
        </div>
    }

    function renderStandard() {
        const {settings} = account;
        const count = Number(settings.invoiceStart);
        const length = settings.invoiceStart.length;
        const nextCount = count + 49
        const repeatMax = Math.max(length - nextCount.toString().length, 0);
        const nextNum = '0'.repeat(repeatMax) + nextCount;
        return (
            <div className='body-container'>
                <div className='flow' onClick={onSelectInvoiceNumberType(InvoiceNumberType.STANDARD)}>
                    <div className='texts-container'>
                        <label className='flow-title'>Standaard</label>
                        <label
                            className='text-example'>Ingesteld: {settings.invoicePrefix}{settings.invoiceStart}</label>
                    </div>
                    <BolmateRadio onSelect={voidFunction} selected={invoiceNumberType === InvoiceNumberType.STANDARD}/>
                </div>
                <div className='info-container'>
                    <AwesomeIcon name='info-circle' className='info-icon'/>
                    <label className='info-text'>
                        Voorbeeld:<br/>
                        Je hebt 50 orders gehad in de afgelopen 30 dagen.<br/>
                        Deze krijgen de
                        nummering {replaceDateVariables(
                        settings.invoicePrefix)}{settings.invoiceStart} tot {replaceDateVariables(
                        settings.invoicePrefix)}{nextNum}<br/>
                        op volgorde chronologisch in de tijd.<br/>
                        De orders die je nieuw binnenkrijgt, tellen door vanaf dit punt.<br/>
                        Die krijgen dus de
                        nummering {replaceDateVariables(settings.invoicePrefix)}{'0'.repeat(
                        repeatMax) + (nextCount + 1)}, {replaceDateVariables(
                        account.settings.invoicePrefix)}{'0'.repeat(repeatMax) + (nextCount + 2)},
                        etc.
                    </label>
                </div>
            </div>

        )
    }

    function renderCustom() {
        const count = Number(invoiceStart);
        const countStandard = Number(account.settings.invoiceStart)
        const length = invoiceStart.length;
        const nextCountStandard = countStandard + 1;
        const repeatMaxStandard = Math.max(length - nextCountStandard.toString().length, 0);
        const nextCount = count + 49;
        const repeatMax = Math.max(length - nextCount.toString().length, 0);
        const selected = invoiceNumberType === InvoiceNumberType.CUSTOM;
        return (
            <div className='body-container'>
                <div className='flow'
                     onClick={onSelectInvoiceNumberType(InvoiceNumberType.CUSTOM)}>

                    <div className='texts-container'>
                        <label className='flow-title'>Anders</label>
                        {(invoiceNumberType === InvoiceNumberType.STANDARD || invoiceNumberType === null) &&
                            <label className='text-example'>Namelijk...</label>}
                    </div>
                    <BolmateRadio onSelect={voidFunction}
                                  selected={invoiceNumberType === InvoiceNumberType.CUSTOM}/>
                    {renderInvoiceNumberEditor(invoiceStart, invoicePrefix, onChangeSetting('invoice_prefix'),
                        onChangeSetting('invoice_start'), 'invoicePrefix', 'invoiceStart', selected)}
                </div>
                <div className='info-container'>
                    <AwesomeIcon name='info-circle' className='info-icon'/>
                    <label className='info-text'>
                        Voorbeeld:<br/>
                        Je hebt 50 orders gehad in de afgelopen 30 dagen.<br/>
                        Je stelt hiervoor een andere factuurnummering in
                        namelijk {replaceDateVariables(invoicePrefix)}{invoiceStart}.<br/>
                        Dan krijgt de import de
                        nummering {replaceDateVariables(invoicePrefix)}{invoiceStart} tot {replaceDateVariables(
                        invoicePrefix)}{'0'.repeat(repeatMax) + nextCount}<br/>
                        op volgorde chronologisch in de tijd.<br/>
                        De orders die je daarna nieuw binnenkrijgt, volgen de standaard factuurnummering.<br/>
                        Deze krijgen dus de
                        nummering {replaceDateVariables(
                        account.settings.invoicePrefix)}{account.settings.invoiceStart}, {replaceDateVariables(
                        account.settings.invoicePrefix)}{'0'.repeat(repeatMaxStandard) + nextCountStandard} etc.</label>
                </div>
            </div>
        )
    }

    function renderInvoiceNumberEditor(invoiceStart: string, invoicePrefix: string,
                                       onChangePrefix: ChangeFunction<string>, onChangeStart: ChangeFunction<string>,
                                       prefixErrorKey: keyof InvoiceSettingsErrors,
                                       startErrorKey: keyof InvoiceSettingsErrors, selected: boolean,
                                       className?: string) {
        return (
            <div
                className={classNames('invoice-number', selected && 'selected', className)}>
                <div className='row'>
                    <BolmateInput value={invoicePrefix} onChange={onChangePrefix}
                                  error={errors[prefixErrorKey]} label='Voortekens *'
                                  onFocus={onResetError(prefixErrorKey)} autoFocus/>
                    <BolmateNumberInput value={invoiceStart}
                                        onChange={onChangeStart} error={errors[startErrorKey]}
                                        label='Beginnen bij *' onFocus={onResetError(startErrorKey)}
                                        wholeNumbers disableSymbols/>
                </div>
                {renderInvoicePreview(invoicePrefix)}
            </div>
        )
    }

    function renderIntro() {
        return (
            <>
                <div className='icon-container background'>
                    <AwesomeIcon name="check" className='check-icon'/>
                </div>
                <label className='text-title'>De koppeling met Bol is gemaakt!</label>
                <div className='text-subtitle-container'>
                    <label className='text-subtitle'>Vanaf nu halen we alle nieuwe orders op bij Bol</label>
                </div>
                <div className='button-container-single'>
                    <BolmateButton onClick={onClickNext} label='Volgende'/>
                </div>
            </>
        )
    }

    function renderImportHistory() {
        return (<>
                <div className='icon-container'>
                    <Icon icon={fileTextFilled} className='invoice-icon'/>
                </div>
                <label className='text-title'>Wil je ook de orders van de afgelopen 30 dagen importeren?</label>
                <div className='text-subtitle-container flex'>
                    <label className='text-subtitle'>Dit kan even duren, afhankelijk van de hoeveelheid orders.<br/>

                    </label>
                    <div className='info-container'>
                        <AwesomeIcon name='info-circle' className='info-icon'/>
                        <label className='info-text import-times'>
                            De facturen zijn pas beschikbaar als het importeren klaar is.<br/>
                            In de tussentijd kun je wel de rest van het platform gebruiken.<br/>
                            Indicatie van de tijd die het duurt:<br/><br/>
                            100 orders = 1 minuut <br/>
                            1000 orders = 1,5 minuten <br/>
                            5000 orders = 2 minuten<br/>
                            ...
                        </label>
                    </div>
                </div>
                {renderYesButton(onChangeImportHistory(true), !!importHistory)}
                {renderNoButton(onChangeImportHistory(false), importHistory === false)}
                <div className='button-container-double'>
                    {renderBackButton()}
                    <BolmateButton onClick={onClickNext} label='Volgende' disabled={importHistory === null}/>
                </div>
            </>

        );
    }

    function renderInvoiceNumber() {
        return (
            <>
                <div className='icon-container'>
                    <Icon icon={fileTextFilled} className='invoice-icon'/>
                </div>
                <label className='text-title'>Welke factuur nummering wil je hiervoor gebruiken?</label>
                <div className='text-subtitle-container'>
                    <label className='text-subtitle'>Wil je dezelfde of een andere factuurnummering gebruiken <br/>voor
                        de import van de orders van de afgelopen 30 dagen?</label>
                </div>
                {renderStandard()}
                {renderCustom()}
                <div className='button-container-double'>
                    {renderBackButton()}
                    <BolmateButton onClick={onClickNext} label='Volgende' disabled={invoiceNumberType === null}/>
                </div>
            </>
        )
    }

    function renderAutoUpload() {
        return (
            <>
                <div className='icon-container'>
                    <Icon icon={fileTextFilled} className='invoice-icon'/>
                </div>
                <label className='text-title'>Facturen automatisch laten uploaden naar Bol?</label>
                <div className='text-subtitle-container flex'>
                    <label className='text-subtitle'>Zo ben je factuuraanvragen voor.<br/>
                    </label>
                    <div className='info-container'>
                        <AwesomeIcon name='info-circle' className='info-icon'/>
                        <label className='info-text left'>
                            Sinds juni 2023 kunnen facturen direct naar het platform van Bol geüpload worden. Als
                            een klant een aanvraag indient voor een factuur, dan ben je verplicht deze binnen 24 uur
                            te uploaden naar Bol. Je kunt met Bolmate factuuraanvragen voor zijn door facturen
                            proactief te laten uploaden. Daarnaast kan Bolmate automatisch jouw factuuraanvragen
                            beantwoorden door de factuur te uploaden.
                            <br/><br/>
                            Je kunt deze keuze altijd nog aanpassen en jouw specifieke upload wensen instellen
                            onder het kopje Aanvragen in het menu.
                        </label>
                    </div>
                </div>
                {renderAutoUploadButton(AutoUploadType.BOTH)}
                {renderAutoUploadButton(AutoUploadType.NONE)}
                <label className='skip-label' onClick={onChooseAutoUpload(AutoUploadType.NONE, true)}>Later
                    beslissen</label>
                <div className='button-container-double'>
                    {renderBackButton()}
                    <BolmateButton onClick={onClickNext} label='Volgende' disabled={autoUpload === null}/>
                </div>
            </>
        );
    }

    function renderAutoUploadButton(value: AutoUploadType.BOTH | AutoUploadType.NONE) {
        const selected = value === autoUpload;
        let title: string;
        if (value === AutoUploadType.BOTH) title = 'Ja';
        else title = 'Nee';
        return (
            <div className='body-container'>
                <div className='flow' onClick={onChooseAutoUpload(value)}>
                    <div className='texts-container'>
                        <label className='flow-title'>{title}</label>
                    </div>
                    <BolmateRadio onSelect={voidFunction} selected={selected}/>
                </div>
            </div>
        )
    }

    function renderAutoUploadOld() {
        return (
            <>
                <div className='icon-container'>
                    <Icon icon={fileTextFilled} className='invoice-icon'/>
                </div>
                <label className='text-title'>Factuuraanvragen voor oudere orders automatisch verwerken?</label>
                <div className='text-subtitle-container flex'>
                    <label className='text-subtitle'>Deze worden verwerkt met een andere factuurnummer reeks.<br/>
                    </label>
                    <div className='info-container'>
                        <AwesomeIcon name='info-circle' className='info-icon'/>
                        <label className='info-text left'>
                            <label className='bold-text'>Factuuraanvragen voor oude orders</label>
                            <br/><br/>
                            Jouw klanten kunnen voor orders tot 2 jaar terug een factuur aanvragen. Het is mogelijk dat
                            wij deze order niet in het systeem hebben, omdat jouw Bolmate account toen nog niet bestond.
                            <br/><br/>
                            We kunnen deze factuuraanvraag wel automatisch beantwoorden met een factuur upload, maar
                            doordat de Belastingdienst vereist dat jouw factuurnummering chronologisch oploopt, kunnen
                            we hiervoor niet het standaard factuurnummer gebruiken.
                            <br/><br/>
                            Je kunt daarom een andere factuurnummerreeks kiezen voor de facturen voor oude orders, op
                            deze manier blijven de nummers chronologisch oplopend.
                        </label>
                    </div>
                </div>
                {renderAutoUploadOldButton(true)}
                {renderAutoUploadOldButton(false)}
                <label className='skip-label' onClick={onChangeAutoUploadOld(null)}>Later
                    beslissen</label>
                <div className='button-container-double'>
                    {renderBackButton()}
                    <BolmateButton onClick={onClickNext} label='Volgende' disabled={autoUpload === null}/>
                </div>
            </>
        );
    }

    function renderAutoUploadOldButton(value: boolean) {
        const selected = value === autoUploadOld;
        const title = value ? 'Ja' : 'Nee';
        return (
            <div className='body-container'>
                <div className='flow' onClick={onChangeAutoUploadOld(value)}>
                    <div className='texts-container'>
                        <label className='flow-title'>{title}</label>
                    </div>
                    <BolmateRadio onSelect={voidFunction} selected={selected}/>
                </div>
            </div>
        )
    }

    function renderAutoUploadOldNumber() {
        return (
            <>
                <div className='icon-container'>
                    <Icon icon={fileTextFilled} className='invoice-icon'/>
                </div>
                <label className='text-title'>Welke factuur nummering wil je hiervoor gebruiken?</label>
                {renderInvoiceNumberEditor(oldInvoiceNumStart, oldInvoiceNumPrefix, onChangeOldInvoiceNumPrefix,
                    onChangeOldInvoiceNumStart, 'oldInvoicePrefix', 'oldInvoiceStart', true, 'old-number-editor')}
                <div className='button-container-double'>
                    {renderBackButton()}
                    <BolmateButton onClick={onClickNext} label='Volgende'/>
                </div>
            </>
        )
    }

    function renderNotifications() {
        return (
            <div className='inner-container'>
                <div className='icon-container'>
                    <Icon icon={Bell} className='invoice-icon'/>
                </div>
                <label className='text-title'>Welke meldingen wil je ontvangen?</label>
                <div className='text-subtitle-container flex'>
                    <label className='text-subtitle'>
                        Selecteer bij welke gebeurtenissen je direct een notificatie per e-mail wilt.<br/>
                        Ik wil een notificatie ontvangen bij:
                    </label>
                    <div className='info-container'>
                        <AwesomeIcon name='info-circle' className='info-icon'/>
                        <label className='info-text left fw'>
                            De notificaties worden met standaard instellingen aangemaakt.<br/>
                            Je kunt deze altijd nog aanpassen op de Notificaties pagina.
                        </label>
                    </div>
                </div>
                <div className='notifications-container'>
                    {renderNotification(NotificationType.REVIEWS)}
                    {renderNotification(NotificationType.BUY_BOX)}
                    {renderNotification(NotificationType.STOCK)}
                    {renderNotification(NotificationType.PRODUCT_HIJACK, {beta: true})}
                    {renderNotification(NotificationType.ORDER)}
                </div>
                <div className='button-container-double'>
                    {renderBackButton()}
                    <BolmateButton onClick={onClickNext} label='Volgende'/>
                </div>
            </div>
        )
    }

    function renderNotification(notificationType: NotificationType, config?: {beta: boolean}) {
        const NotificationTypeParams: Record<NotificationType, NotificationTypeParam> = {
            [NotificationType.REVIEWS]: {
                icon: 'star', title: 'Nieuwe reviews', description: 'Ontvang een e-mail bij nieuwe reviews'
            },
            [NotificationType.PRODUCT_HIJACK]: {
                icon: 'user-shield', title: 'Productkaping notificatie',
                description: 'Ontvang een e-mail wanneer een nieuwe verkoper op jouw product verschijnt'
            },
            [NotificationType.BUY_BOX]: {
                icon: 'trophy', title: 'Koopblok verandering', description: 'Een e-mail wanneer het koopblok verandert'
            },
            [NotificationType.STOCK]: {
                icon: 'box-open', title: 'Lage voorraad',
                description: 'Krijg een e-mail als jouw voorraad onder de 20 is'
            },
            [NotificationType.ORDER]: {
                icon: 'truck-fast', title: 'Nieuwe bestellingen', description: 'Ontvang een e-mail bij nieuwe orders'
            },
        }
        const {icon, title, description} = NotificationTypeParams[notificationType];
        return (
            <div className='notification-container' onClick={onToggleNotification(notificationType)}>
                <AwesomeIcon name={icon} className='flow-icon'/>
                <div className='texts-container'>
                    <label className='flow-title'>{title} {config?.beta &&
                        <BetaNotificationLabel className='hijack-beta-label'/>}</label>
                    <label
                        className='text-example'>{description}</label>
                </div>
                <Checkbox onCheck={voidFunction} checked={enableNotifications.has(notificationType)}/>
            </div>
        )
    }

    function renderSummary() {
        return (
            <>
                <Loader active={loading}/>
                <div className='icon-container'>
                    <Icon icon="icons8:finish-flag" className='invoice-icon'/>
                </div>
                <label className='text-title'>Klaar voor de start!</label>
                {renderHistorySummary()}
                <div className='button-container-double'>
                    {renderBackButton()}
                    <BolmateButton onClick={onClickNext} label='Start'/>
                </div>
            </>
        )
    }

    function renderHistorySummary() {
        if (!importHistory) return null;
        return (
            <div className='text-subtitle-container flex'>
                <label className='text-subtitle'>
                    We gaan nu jouw orders importeren.<br/>
                    Dit kan even duren, afhankelijk van het aantal orders.<br/>
                    In de tussentijd kun je de rest van het platform gewoon gebruiken.<br/><br/>
                    De facturen worden vanaf nu automatisch aangemaakt.<br/>
                    Je kunt Bolmate nu verder naar wens instellen en gaan gebruiken.
                </label>
                <div className='info-container'>
                    <AwesomeIcon name='info-circle' className='info-icon'/>
                    <label className='info-text'>
                        De facturen zijn pas beschikbaar als het importeren klaar is.<br/>
                        In de tussentijd kun je wel de rest van het platform gebruiken.<br/>
                        Indicatie van de tijd die het duurt:<br/><br/>
                        100 orders = 1 minuut <br/>
                        1000 orders = 1,5 minuten <br/>
                        5000 orders = 2 minuten<br/>
                        ...
                    </label>
                </div>
            </div>
        );
    }

    function renderStep(): ReactElement {
        switch (step) {
            case ConnectionSettingsStep.INTRO:
                return renderIntro();
            case ConnectionSettingsStep.IMPORT_HISTORY:
                return renderImportHistory();
            case ConnectionSettingsStep.INVOICE_NUMBER:
                return renderInvoiceNumber();
            case ConnectionSettingsStep.AUTO_UPLOAD:
                return renderAutoUpload();
            case ConnectionSettingsStep.AUTO_UPLOAD_OLD:
                return renderAutoUploadOld();
            case ConnectionSettingsStep.AUTO_UPLOAD_OLD_NUMBER:
                return renderAutoUploadOldNumber();
            case ConnectionSettingsStep.NOTIFICATIONS:
                return renderNotifications();
            case ConnectionSettingsStep.COMPLETE:
            case ConnectionSettingsStep.COMPLETE_IMPORT:
                return renderSummary();
        }
    }


    return (
        <div className='connection-complete'>
            <Image name='logo' className='logo'/>
            <div
                className={classNames('main-container',
                    [ConnectionSettingsStep.INTRO, ConnectionSettingsStep.COMPLETE].includes(step) && 'align-center')}>
                {renderStep()}
            </div>
        </div>
    );
}